import { action, atom } from '@reatom/framework'
import BigNumber from 'bignumber.js'
import i18next from 'i18next'

import { filtersHash } from '@blade/blockchain-types'
import { tradeApi } from '@blade/socket-types'
import { delugeIdAtom } from '@root/entities/auth'
import { Network } from '@root/entities/network/model'
import { PairAddress } from '@root/entities/pair/model'
import {
  confirmTransactionUpdateEvent,
  pairEventsEvent,
  pairPartialUpdateEvent,
  pairPriceUpdateEvent,
  pairUpdateNoBufferEvent,
  pairUpdateEventsBuffer,
  poolActivityEvent,
  referralCodeUpdateEvent,
  supportMessageCreatedEvent,
  supportTicketUpdatedEvent,
  userOrderEvent,
  userPositionUpdateEvent,
  walletBalanceUpdateEvent,
  pairUpdateMigrationEvent,
} from '@root/entities/realtime'
import { TxHash } from '@root/entities/transaction/model'
import {
  type EventHandlerWithCtxMap,
  reatomSocket,
} from '@root/shared/api/socket'
import { reatomEventSubscription } from '@root/shared/api/socket/reatom-event-subscription'
import { environment, LOG_WS_EVENTS_ENABLED } from '@root/shared/environment'
import { showSnackbarAction } from '@root/shared/ui-kit'

import * as PoolHolderChartsUpdate from './model/poolholder-chart-update-data'

import type { PoolHolderChartUpdate } from '@blade/blockchain-types'
// import type { UnauthorizedData } from '@blade/socket-types'
import type { TypeHelper } from '@blade/std-lib'
import type { DelugeId } from '@root/entities/user-short'
import type { Socket } from 'socket.io-client'

type EmitParams<T extends tradeApi.ClientEvent> = Parameters<
  tradeApi.ClientToServerEvents[T]
>[0]

export const trackingWalletAlertEvent =
  // eslint-disable-next-line @reatom/unit-naming-rule
  action<tradeApi.TrackingWalletAlertData>('trackingWalletAlertEvent')

export const unauthorizedEvent =
  // eslint-disable-next-line @reatom/unit-naming-rule
  action<void>('UnauthorizedEvent')

export const holderChartUpdateEvent = action<
  [PoolHolderChartUpdate],
  PoolHolderChartsUpdate.PoolHolderChartsUpdate
>((ctx, param) => {
  return PoolHolderChartsUpdate.fromDto(param)
}, 'holderChartUpdateEvent')

const listenEventsMap: EventHandlerWithCtxMap<tradeApi.ServerToClientEvents> = {
  [tradeApi.ServerEvent.Unauthorized]: (ctx) => {
    unauthorizedEvent(ctx)
  },
  [tradeApi.ServerEvent.Authenticated]: () => {
    if (LOG_WS_EVENTS_ENABLED) {
      // eslint-disable-next-line no-console
      console.log('[WebSocket] Authenticated')
    }
  },
  [tradeApi.ServerEvent.PairUpdate]: (ctx, data) => {
    if (data.kind === 'partial') {
      pairPartialUpdateEvent(ctx, data)
      return
    }
    if (data.kind === 'migration') {
      pairUpdateMigrationEvent(ctx, data)
      return
    }

    pairUpdateEventsBuffer.emitEvent(ctx, data)
    pairUpdateNoBufferEvent(ctx, data)
  },
  [tradeApi.ServerEvent.NFTGenerationCompleted]: () => {
    // TODO: implement event handler
  },
  [tradeApi.ServerEvent.NFTGenerationError]: () => {
    // TODO: implement event handler
  },
  [tradeApi.ServerEvent.NFTGenerationProgress]: () => {
    // TODO: implement event handler
  },
  [tradeApi.ServerEvent.UserOnlineStatusUpdate]: (_ctx, _data) => {
    // TODO: implement event handler
  },
  [tradeApi.ServerEvent.SupportTicketUpdated]: (ctx, data) => {
    supportTicketUpdatedEvent(ctx, data)
  },
  [tradeApi.ServerEvent.SupportMessageCreated]: (ctx, data) => {
    supportMessageCreatedEvent(ctx, data)
  },
  [tradeApi.ServerEvent.ReferralCodeUpdate]: (ctx, data) => {
    referralCodeUpdateEvent(ctx, data)
  },
  [tradeApi.ServerEvent.PairEvents]: (ctx, data) => {
    if (data.updateType === 'update-existing') {
      for (const pair of data.pools) {
        pairUpdateEventsBuffer.emitEvent(ctx, pair)
        pairUpdateNoBufferEvent(ctx, pair)
      }
      return
    }
    pairEventsEvent(ctx, data)
  },
  [tradeApi.ServerEvent.PairPriceUpdate]: (ctx, data) => {
    pairPriceUpdateEvent(ctx, {
      ...data,
      priceNative: BigNumber(data.priceNative),
      priceUsd: BigNumber(data.priceUsd),
      pairAddress: PairAddress(data.pairAddress),
      network: Network.fromSupportedNetwork(data.network),
      priceChangePercents: {
        '5M': BigNumber(data.priceChangePercents['5M']),
        '1H': BigNumber(data.priceChangePercents['1H']),
        '6H': BigNumber(data.priceChangePercents['6H']),
        '1D': BigNumber(data.priceChangePercents['1D']),
        ALL: BigNumber(data.priceChangePercents.ALL),
      },
      updatedAt: new Date(data.updatedAt),
    })
  },
  [tradeApi.ServerEvent.UserOrder]: (ctx, data) => {
    userOrderEvent(ctx, data)
  },
  [tradeApi.ServerEvent.UserPositionUpdate]: (ctx, data) => {
    userPositionUpdateEvent(ctx, data)
  },
  [tradeApi.ServerEvent.TrackingWalletAlert]: (ctx, data) => {
    trackingWalletAlertEvent(ctx, data)
  },
  [tradeApi.ServerEvent.HolderChartUpdate]: (ctx, data) => {
    holderChartUpdateEvent(ctx, data)
  },
  [tradeApi.ServerEvent.WalletBalanceUpdate]: (ctx, data) => {
    walletBalanceUpdateEvent(ctx, data)
  },
  [tradeApi.ServerEvent.ConfirmTransactionUpdate]: (ctx, data) => {
    confirmTransactionUpdateEvent(ctx, { ...data, txHash: TxHash(data.txHash) })
  },
  [tradeApi.ServerEvent.PoolActivity]: (ctx, data) => {
    poolActivityEvent(ctx, data)
  },
}

export interface PairInfoUnsubscribeKey
  extends EmitParams<tradeApi.ClientEvent.PairInfoUnsubscribe> {
  pairAddress: PairAddress
}
export interface PairInfoSubscribeKey
  extends EmitParams<tradeApi.ClientEvent.PairInfoSubscribe> {
  pairAddress: PairAddress
}

export interface UserOrderSubscribeKey
  extends TypeHelper.OverrideKeys<
    EmitParams<tradeApi.ClientEvent.UserOrderSubscribe>,
    {
      network: Network.Network
      delugeId: DelugeId
    }
  > {}

export interface UserOrderUnsubscribeKey
  extends TypeHelper.OverrideKeys<
    EmitParams<tradeApi.ClientEvent.UserOrderUnsubscribe>,
    {
      network: Network.Network
      delugeId: DelugeId
    }
  > {}

export interface UserPositionsSubscribeKey
  extends TypeHelper.OverrideKeys<
    EmitParams<tradeApi.ClientEvent.UserPositionsSubscribe>,
    { delugeId: DelugeId; network: Network.Network }
  > {}

export interface UserPositionsUnsubscribeKey
  extends TypeHelper.OverrideKeys<
    EmitParams<tradeApi.ClientEvent.UserPositionsSubscribe>,
    {
      delugeId: DelugeId
      network: Network.Network
    }
  > {}

export interface PairPriceSubscribeKey
  extends TypeHelper.OverrideKeys<
    EmitParams<tradeApi.ClientEvent.PairPriceSubscribe>,
    {
      network: Network.Network
      pairAddresses: ReadonlyArray<PairAddress>
    }
  > {}
export interface PairPriceUnsubscribeKey
  extends TypeHelper.OverrideKeys<
    EmitParams<tradeApi.ClientEvent.PairPriceUnsubscribe>,
    {
      network: Network.Network
      pairAddresses: ReadonlyArray<PairAddress>
    }
  > {}

export interface PairEventsSubscribeKey
  extends EmitParams<tradeApi.ClientEvent.PairEventsSubscribe> {}
export interface PairEventsUnsubscribeKey
  extends EmitParams<tradeApi.ClientEvent.PairEventsUnsubscribe> {}

export type TrackingWalletAlertSubscribeArgs = TypeHelper.OmitKey<
  EmitParams<tradeApi.ClientEvent.WalletsTrackerSubscribe>,
  'network' | 'delugeId'
> & {
  network: Network.Network
  delugeId: DelugeId
}
export type TrackingWalletAlertUnsubscribeArgs = TypeHelper.OmitKey<
  EmitParams<tradeApi.ClientEvent.WalletsTrackerUnsubscribe>,
  'network' | 'delugeId'
> & {
  network: Network.Network
  delugeId: DelugeId
}
export interface HolderChartSubscribeKey
  extends TypeHelper.OverrideKeys<
    EmitParams<tradeApi.ClientEvent.HolderChartSubscribe>,
    {
      pairAddress: PairAddress
      network: Network.Network
    }
  > {}
export interface HolderChartUnsubscribeKey
  extends TypeHelper.OverrideKeys<
    EmitParams<tradeApi.ClientEvent.HolderChartUnsubscribe>,
    {
      pairAddress: PairAddress
      network: Network.Network
    }
  > {}

export interface WalletBalanceSubscribeKey
  extends TypeHelper.OverrideKeys<
    EmitParams<tradeApi.ClientEvent.WalletBalanceSubscribe>,
    {
      network: Network.Network
      delugeId: DelugeId
    }
  > {}
export interface WalletBalanceUnsubscribeKey
  extends TypeHelper.OverrideKeys<
    EmitParams<tradeApi.ClientEvent.WalletBalanceUnsubscribe>,
    { network: Network.Network; delugeId: DelugeId }
  > {}

export interface ConfirmTransactionSubscribeKey
  extends TypeHelper.OverrideKeys<
    EmitParams<tradeApi.ClientEvent.ConfirmTransactionSubscribe>,
    { network: Network.Network; delugeId: DelugeId }
  > {}
export interface ConfirmTransactionUnsubscribeKey
  extends TypeHelper.OverrideKeys<
    EmitParams<tradeApi.ClientEvent.ConfirmTransactionUnsubscribe>,
    { network: Network.Network; delugeId: DelugeId }
  > {}

export interface PoolActivitySubscribeArgs
  extends TypeHelper.OverrideKeys<
    EmitParams<tradeApi.ClientEvent.PoolsActivitySubscribe>,
    { network: Network.Network }
  > {}
export interface PoolActivityUnsubscribeArgs
  extends TypeHelper.OverrideKeys<
    EmitParams<tradeApi.ClientEvent.PoolsActivityUnsubscribe>,
    { network: Network.Network }
  > {}

/**
 * Socket io wrapper around main namespace
 */
export const reatomWebsocketMain = () => {
  const socketLatencyAtom = atom<number | undefined>(
    undefined,
    'socketLatencyAtom',
  )
  const { initializeSocketAction, emitAction, socketAtom } = reatomSocket({
    namespace: '/',
    listenEventsMap,
    host: environment.tradeApiHost,
  })
  const latencyCheckIntervalId = atom<number | undefined>(
    undefined,
    'latencyCheckIntervalId',
  )
  const initializeSocketLatencyCheckAction = action((ctx, socket: Socket) => {
    // Clear before starting, so calling this twice cannot leave an orphan.
    // It used to only overwrite the atom, and the id it overwrote was gone for
    // good: startup ran this once directly and once more from the `connect`
    // that followed, so every session leaked a ping interval nothing could
    // ever stop.
    clearInterval(ctx.get(latencyCheckIntervalId))

    const intervalId = window.setInterval(() => {
      if (!socket.connected) {
        return
      }

      const delugeId = ctx.get(delugeIdAtom)
      const before = performance.now()

      emitAction(
        ctx,
        tradeApi.ClientEvent.LatencyPing,
        {
          delugeId,
          currentPage: window.location.toString(),
        },
        () => {
          const now = performance.now()
          const latency = now - before

          socketLatencyAtom(ctx, Math.round(latency))
        },
      )
    }, 1000)
    latencyCheckIntervalId(ctx, intervalId)
  }, 'initializeSocketLatencyCheckAction')

  // eslint-disable-next-line @reatom/unit-naming-rule
  const pairInfoSubscription = reatomEventSubscription({
    subscribe: (ctx, req: PairInfoSubscribeKey) => {
      emitAction(ctx, tradeApi.ClientEvent.PairInfoSubscribe, req)
    },
    unsubscribe: (ctx, req: PairInfoUnsubscribeKey) => {
      emitAction(ctx, tradeApi.ClientEvent.PairInfoUnsubscribe, req)
    },
    deriveKeyFromSubscribeArgs: (args) =>
      `${tradeApi.ClientEvent.PairInfoSubscribe}_${args.network}_${args.pairAddress}_${args.network}`,
    deriveKeyFromUnsubscribeArgs: (args) =>
      `${tradeApi.ClientEvent.PairInfoSubscribe}_${args.network}_${args.pairAddress}_${args.network}`,
  })

  // eslint-disable-next-line @reatom/unit-naming-rule
  const userOrderSubscription = reatomEventSubscription({
    subscribe: (ctx, req: UserOrderSubscribeKey) => {
      emitAction(ctx, tradeApi.ClientEvent.UserOrderSubscribe, {
        ...req,
        network: Network.toSupportedNetwork(req.network),
      })
    },
    unsubscribe: (ctx, req: UserOrderUnsubscribeKey) => {
      emitAction(ctx, tradeApi.ClientEvent.UserOrderUnsubscribe, {
        ...req,
        network: Network.toSupportedNetwork(req.network),
      })
    },
    deriveKeyFromSubscribeArgs: (args) =>
      `${tradeApi.ServerEvent.UserOrder}_${args.delugeId}_${args.network}`,
    deriveKeyFromUnsubscribeArgs: (args) =>
      `${tradeApi.ServerEvent.UserOrder}_${args.delugeId}_${args.network}`,
  })

  // eslint-disable-next-line @reatom/unit-naming-rule
  const userPositionsSubscription = reatomEventSubscription({
    subscribe: (ctx, req: UserPositionsSubscribeKey) => {
      emitAction(ctx, tradeApi.ClientEvent.UserPositionsSubscribe, {
        ...req,
        network: Network.toSupportedNetwork(req.network),
      })
    },
    unsubscribe: (ctx, req: UserPositionsUnsubscribeKey) => {
      emitAction(ctx, tradeApi.ClientEvent.UserPositionsUnsubscribe, {
        ...req,
        network: Network.toSupportedNetwork(req.network),
      })
    },
    deriveKeyFromSubscribeArgs: (args) =>
      `${tradeApi.ServerEvent.UserPositionUpdate}_${args.delugeId}_${args.network}`,
    deriveKeyFromUnsubscribeArgs: (args) =>
      `${tradeApi.ServerEvent.UserPositionUpdate}_${args.delugeId}_${args.network}`,
  })

  // eslint-disable-next-line @reatom/unit-naming-rule
  const pairPriceSubscription = reatomEventSubscription({
    subscribe: (ctx, req: PairPriceSubscribeKey) => {
      emitAction(ctx, tradeApi.ClientEvent.PairPriceSubscribe, {
        ...req,
        network: Network.toSupportedNetwork(req.network),
      })
    },
    unsubscribe: (ctx, req: PairPriceUnsubscribeKey) => {
      emitAction(ctx, tradeApi.ClientEvent.PairPriceUnsubscribe, {
        ...req,
        network: Network.toSupportedNetwork(req.network),
      })
    },
    deriveKeyFromSubscribeArgs: (args) =>
      `${tradeApi.ServerEvent.PairPriceUpdate}_${args.network}_${args.pairAddresses.join('_')}`,
    deriveKeyFromUnsubscribeArgs: (args) =>
      `${tradeApi.ServerEvent.PairPriceUpdate}_${args.network}_${args.pairAddresses.join('_')}`,
  })

  // eslint-disable-next-line @reatom/unit-naming-rule
  const pairEventsSubscription = reatomEventSubscription({
    subscribe: (ctx, req: PairEventsSubscribeKey) => {
      emitAction(ctx, tradeApi.ClientEvent.PairEventsSubscribe, req)
    },
    unsubscribe: (ctx, req: PairEventsUnsubscribeKey) => {
      emitAction(ctx, tradeApi.ClientEvent.PairEventsUnsubscribe, req)
    },
    deriveKeyFromSubscribeArgs: (args) =>
      `${tradeApi.ServerEvent.PairEvents}_${args.network}_${args.events.join('_')}_${filtersHash(args.filters) ?? 'none'}`,
    deriveKeyFromUnsubscribeArgs: (args) =>
      `${tradeApi.ServerEvent.PairEvents}_${args.network}_${args.events.join('_')}_${filtersHash(args.filters) ?? 'none'}`,
  })
  // eslint-disable-next-line @reatom/unit-naming-rule
  const holderChartSubscription = reatomEventSubscription({
    subscribe: (ctx, req: HolderChartSubscribeKey) => {
      emitAction(ctx, tradeApi.ClientEvent.HolderChartSubscribe, {
        ...req,
        network: Network.toSupportedNetwork(req.network),
      })
    },
    unsubscribe: (ctx, req: HolderChartUnsubscribeKey) => {
      emitAction(ctx, tradeApi.ClientEvent.HolderChartUnsubscribe, {
        ...req,
        network: Network.toSupportedNetwork(req.network),
      })
    },
    deriveKeyFromSubscribeArgs: (args) =>
      `${tradeApi.ServerEvent.HolderChartUpdate}_${args.network}_${args.pairAddress}`,
    deriveKeyFromUnsubscribeArgs: (args) =>
      `${tradeApi.ServerEvent.HolderChartUpdate}_${args.network}_${args.pairAddress}`,
  })

  // eslint-disable-next-line @reatom/unit-naming-rule
  const trackingWalletAlertSubscription = reatomEventSubscription({
    subscribe: (ctx, req: TrackingWalletAlertSubscribeArgs) => {
      emitAction(ctx, tradeApi.ClientEvent.WalletsTrackerSubscribe, {
        ...req,
        network: Network.toSupportedNetwork(req.network),
      })
    },
    unsubscribe: (ctx, req: TrackingWalletAlertUnsubscribeArgs) => {
      emitAction(ctx, tradeApi.ClientEvent.WalletsTrackerUnsubscribe, {
        ...req,
        network: Network.toSupportedNetwork(req.network),
      })
    },
    deriveKeyFromSubscribeArgs: (args) =>
      `${tradeApi.ServerEvent.TrackingWalletAlert}_${args.network}`,
    deriveKeyFromUnsubscribeArgs: (args) =>
      `${tradeApi.ServerEvent.TrackingWalletAlert}_${args.network}`,
  })

  // eslint-disable-next-line @reatom/unit-naming-rule
  const walletBalanceSubscription = reatomEventSubscription({
    subscribe: (ctx, req: WalletBalanceSubscribeKey) => {
      emitAction(ctx, tradeApi.ClientEvent.WalletBalanceSubscribe, {
        ...req,
        network: Network.toSupportedNetwork(req.network),
      })
    },
    unsubscribe: (ctx, req: WalletBalanceUnsubscribeKey) => {
      emitAction(ctx, tradeApi.ClientEvent.WalletBalanceUnsubscribe, {
        ...req,
        network: Network.toSupportedNetwork(req.network),
      })
    },
    deriveKeyFromSubscribeArgs: (args) =>
      `${tradeApi.ServerEvent.WalletBalanceUpdate}_${args.network}_${args.delugeId}`,
    deriveKeyFromUnsubscribeArgs: (args) =>
      `${tradeApi.ServerEvent.WalletBalanceUpdate}_${args.network}_${args.delugeId}`,
  })

  // eslint-disable-next-line @reatom/unit-naming-rule
  const confirmTransactionSubscription = reatomEventSubscription({
    subscribe: (ctx, req: ConfirmTransactionSubscribeKey) => {
      emitAction(ctx, tradeApi.ClientEvent.ConfirmTransactionSubscribe, {
        ...req,
        network: Network.toSupportedNetwork(req.network),
      })
    },
    unsubscribe: (ctx, req: ConfirmTransactionUnsubscribeKey) => {
      emitAction(ctx, tradeApi.ClientEvent.ConfirmTransactionUnsubscribe, {
        ...req,
        network: Network.toSupportedNetwork(req.network),
      })
    },
    deriveKeyFromSubscribeArgs: (args) =>
      `${tradeApi.ServerEvent.ConfirmTransactionUpdate}_${args.network}_${args.delugeId}`,
    deriveKeyFromUnsubscribeArgs: (args) =>
      `${tradeApi.ServerEvent.ConfirmTransactionUpdate}_${args.network}_${args.delugeId}`,
  })

  // eslint-disable-next-line @reatom/unit-naming-rule
  const poolActivitySubscription = reatomEventSubscription({
    subscribe: (ctx, req: PoolActivitySubscribeArgs) => {
      emitAction(ctx, tradeApi.ClientEvent.PoolsActivitySubscribe, {
        ...req,
        network: Network.toSupportedNetwork(req.network),
      })
    },
    unsubscribe: (ctx, req: PoolActivityUnsubscribeArgs) => {
      emitAction(ctx, tradeApi.ClientEvent.PoolsActivityUnsubscribe, {
        ...req,
        network: Network.toSupportedNetwork(req.network),
      })
    },
    deriveKeyFromSubscribeArgs: (args) =>
      `${tradeApi.ServerEvent.PoolActivity}_${args.network}`,
    deriveKeyFromUnsubscribeArgs: (args) =>
      `${tradeApi.ServerEvent.PoolActivity}_${args.network}`,
  })

  // eslint-disable-next-line @reatom/unit-naming-rule
  const supportTicketChatSubscription = reatomEventSubscription({
    subscribe: (ctx, req: { ticketId: string }) => {
      emitAction(ctx, tradeApi.ClientEvent.SupportTicketSubscribe, {
        ...req,
      })
    },
    unsubscribe: (ctx, req: { ticketId: string }) => {
      emitAction(ctx, tradeApi.ClientEvent.SupportTicketUnsubscribe, {
        ...req,
      })
    },
    deriveKeyFromSubscribeArgs: (args) =>
      `${tradeApi.ClientEvent.SupportTicketSubscribe}_${args.ticketId}`,
    deriveKeyFromUnsubscribeArgs: (args) =>
      `${tradeApi.ClientEvent.SupportTicketSubscribe}_${args.ticketId}`,
  })

  // eslint-disable-next-line @reatom/unit-naming-rule
  const supportTicketUpdatesSubscription = reatomEventSubscription({
    subscribe: (ctx) => {
      emitAction(ctx, tradeApi.ClientEvent.SupportTicketSubscribe)
    },
    unsubscribe: (ctx) => {
      emitAction(ctx, tradeApi.ClientEvent.SupportTicketUnsubscribe)
    },
    deriveKeyFromSubscribeArgs: () =>
      `${tradeApi.ClientEvent.SupportUserTicketsSubscribe}`,
    deriveKeyFromUnsubscribeArgs: () =>
      `${tradeApi.ClientEvent.SupportUserTicketsSubscribe}`,
  })

  const sendWsTicketAction = action((ctx, wsTicket: string) => {
    const socket = ctx.get(socketAtom)
    if (!socket) {
      return
    }
    socket.emit(tradeApi.ClientEvent.Authenticate, { wsTicket })
  }, 'sendWsTicketAction')

  const reemitEventsActions = action((ctx) => {
    pairInfoSubscription.reemitSubscriptionsActions(ctx)
    userOrderSubscription.reemitSubscriptionsActions(ctx)
    userPositionsSubscription.reemitSubscriptionsActions(ctx)
    pairPriceSubscription.reemitSubscriptionsActions(ctx)
    pairEventsSubscription.reemitSubscriptionsActions(ctx)
    walletBalanceSubscription.reemitSubscriptionsActions(ctx)
    confirmTransactionSubscription.reemitSubscriptionsActions(ctx)
    trackingWalletAlertSubscription.reemitSubscriptionsActions(ctx)
    holderChartSubscription.reemitSubscriptionsActions(ctx)
    poolActivitySubscription.reemitSubscriptionsActions(ctx)
    supportTicketChatSubscription.reemitSubscriptionsActions(ctx)
    supportTicketUpdatesSubscription.reemitSubscriptionsActions(ctx)
  }, 'reemitEventsActions')

  /**
   * Fired when the socket comes back, not when it first arrives.
   *
   * Re-subscribing restores the flow but not what was missed while it was
   * down, so a list kept current by events has to be read again. The first
   * connect is left out: whoever subscribed has just loaded it.
   */
  const reconnectedAction = action((_ctx) => {}, 'reconnectedAction')
  let hasConnected = false

  const initializeSocketInternalAction = action((ctx) => {
    const socket = initializeSocketAction(ctx)
    initializeSocketLatencyCheckAction(ctx, socket)
    pairUpdateEventsBuffer.initializeAction(ctx)

    socket.on('connect', () => {
      // Re-send on every connect, recovered or not.
      //
      // `recovered` is Socket.IO's word for "I restored your session", and
      // trusting it cost us a class of silent failures: the socket comes back,
      // reports itself recovered, and the feed never speaks again until the
      // page is reloaded. Re-subscribing is idempotent — the server joins a
      // room the socket is already in — so the saved round trip was never
      // worth the risk of being wrong.
      //
      // The latency check has to be restarted here too: `disconnect` clears
      // its interval unconditionally, so behind the old guard a recovered
      // reconnect left it dead for the rest of the session.
      initializeSocketLatencyCheckAction(ctx, socket)
      reemitEventsActions(ctx)
      if (hasConnected) {
        reconnectedAction(ctx)
      }
      hasConnected = true
    })

    socket.on('disconnect', () => {
      clearInterval(ctx.get(latencyCheckIntervalId))
      socketLatencyAtom(ctx, undefined)
      showSnackbarAction(ctx, {
        text: i18next.t('latency.disconnected.description'),
        intent: 'error',
      })
    })
  }, 'initializeSocketInternalAction')

  return {
    socketLatencyAtom,
    initializeSocketAction: initializeSocketInternalAction,
    reconnectedAction,

    pairInfoSubscribeAction: pairInfoSubscription.subscribe,
    pairInfoUnsubscribeAction: pairInfoSubscription.unsubscribe,

    userOrderSubscribeAction: userOrderSubscription.subscribe,
    userOrderUnsubscribeAction: userOrderSubscription.unsubscribe,

    userPositionsSubscribeAction: userPositionsSubscription.subscribe,
    userPositionsUnsubscribeAction: userPositionsSubscription.unsubscribe,

    pairPriceSubscribeAction: pairPriceSubscription.subscribe,
    pairPriceUnsubscribeAction: pairPriceSubscription.unsubscribe,

    pairEventsSubscribeAction: pairEventsSubscription.subscribe,
    pairEventsUnsubscribeAction: pairEventsSubscription.unsubscribe,

    walletBalanceSubscribeAction: walletBalanceSubscription.subscribe,
    walletBalanceUnsubscribeAction: walletBalanceSubscription.unsubscribe,

    confirmTransactionSubscribeAction: confirmTransactionSubscription.subscribe,
    confirmTransactionUnsubscribeAction:
      confirmTransactionSubscription.unsubscribe,

    trackingWalletAlertSubscribeAction:
      trackingWalletAlertSubscription.subscribe,
    trackingWalletAlertUnsubscribeAction:
      trackingWalletAlertSubscription.unsubscribe,

    holderChartSubscribeAction: holderChartSubscription.subscribe,
    holderChartUnsubscribeAction: holderChartSubscription.unsubscribe,

    poolActivitySubscribeAction: poolActivitySubscription.subscribe,
    poolActivityUnsubscribeAction: poolActivitySubscription.unsubscribe,

    supportTicketChatSubscribeAction: supportTicketChatSubscription.subscribe,
    supportTicketChatUnsubscribeAction:
      supportTicketChatSubscription.unsubscribe,

    supportTicketUpdatesSubscribeAction:
      supportTicketUpdatesSubscription.subscribe,
    supportTicketUpdatesUnsubscribeAction:
      supportTicketUpdatesSubscription.unsubscribe,

    sendWsTicket: sendWsTicketAction,
  }
}

export type ReatomWebsocketMain = ReturnType<typeof reatomWebsocketMain>

if (LOG_WS_EVENTS_ENABLED) {
  walletBalanceUpdateEvent.onCall((ctx, data) => {
    // eslint-disable-next-line no-console
    console.log('[WebSocket] walletBalanceUpdateEvent', data)
  })
  userPositionUpdateEvent.onCall((ctx, data) => {
    // eslint-disable-next-line no-console
    console.log('[WebSocket] userPositionUpdateEvent', data)
  })
  userOrderEvent.onCall((ctx, data) => {
    // eslint-disable-next-line no-console
    console.log('[WebSocket] userOrderEvent', data)
  })
  supportTicketUpdatedEvent.onCall((ctx, data) => {
    // eslint-disable-next-line no-console
    console.log('[WebSocket] supportTicketUpdatedEvent', data)
  })
  supportMessageCreatedEvent.onCall((ctx, data) => {
    // eslint-disable-next-line no-console
    console.log('[WebSocket] supportMessageCreatedEvent', data)
  })
  confirmTransactionUpdateEvent.onCall((ctx, data) => {
    // eslint-disable-next-line no-console
    console.log('[WebSocket] confirmTransactionUpdateEvent', data)
  })
}
